import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { WorkLog, WorkLogStatus } from '../../types';
import { createWorkLog, updateWorkLog, addIssuesToWorkLog, removeIssueFromWorkLog } from '../../services/api/workLog';
import { useToast } from '../ToastContainer';
import { format } from 'date-fns';
import IssueSelector from './IssueSelector';

interface WorkLogEditorProps {
  projectId: string;
  workLog?: WorkLog | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const WorkLogEditor: React.FC<WorkLogEditorProps> = ({
  projectId,
  workLog,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    title: '',
    startDate: '',
    endDate: '',
    status: WorkLogStatus.DRAFT,
    communityName: '',
    managementCompany: '',
    dutyStaff: '',
    morningWorkItems: '',
    afternoonWorkItems: '',
    otherWorkItems: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIssueSelectorOpen, setIsIssueSelectorOpen] = useState(false);
  const [selectedIssueIds, setSelectedIssueIds] = useState<string[]>([]);
  const { showError, showSuccess } = useToast();

  useEffect(() => {
    if (workLog) {
      setFormData({
        title: workLog.title || '',
        startDate: workLog.startDate ? format(new Date(workLog.startDate), 'yyyy-MM-dd') : '',
        endDate: workLog.endDate ? format(new Date(workLog.endDate), 'yyyy-MM-dd') : '',
        status: workLog.status || WorkLogStatus.DRAFT,
        communityName: workLog.communityName || '',
        managementCompany: workLog.managementCompany || '',
        dutyStaff: workLog.dutyStaff || '',
        morningWorkItems: workLog.morningWorkItems || '',
        afternoonWorkItems: workLog.afternoonWorkItems || '',
        otherWorkItems: workLog.otherWorkItems || ''
      });
      // 設定已選擇的議題
      if (workLog.selectedIssues) {
        setSelectedIssueIds(workLog.selectedIssues.map(wi => wi.issueId));
      }
    } else {
      // 新建時設定預設值
      const today = new Date();
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);

      setFormData({
        title: `工作日誌 ${format(today, 'yyyy-MM-dd')}`,
        startDate: format(lastWeek, 'yyyy-MM-dd'),
        endDate: format(today, 'yyyy-MM-dd'),
        status: WorkLogStatus.DRAFT,
        communityName: '',
        managementCompany: '',
        dutyStaff: '',
        morningWorkItems: '',
        afternoonWorkItems: '',
        otherWorkItems: ''
      });
    }
  }, [workLog]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.startDate || !formData.endDate) {
      showError('請填寫必要欄位');
      return;
    }

    setIsSubmitting(true);

    try {
      const data = {
        ...formData,
        projectId,
        morningWorkItems: formData.morningWorkItems.trim() ? formData.morningWorkItems : null,
        afternoonWorkItems: formData.afternoonWorkItems.trim() ? formData.afternoonWorkItems : null,
        otherWorkItems: formData.otherWorkItems.trim() ? formData.otherWorkItems : null
      };

      let savedWorkLog;
      if (workLog) {
        savedWorkLog = await updateWorkLog(workLog.id, data);
        showSuccess('更新成功');
      } else {
        savedWorkLog = await createWorkLog(data);
        showSuccess('建立成功');
      }

      // 處理議題關聯
      if (savedWorkLog) {
        const currentIssueIds = workLog?.selectedIssues?.map(wi => wi.issueId) || [];
        const toAdd = selectedIssueIds.filter(id => !currentIssueIds.includes(id));
        const toRemove = currentIssueIds.filter(id => !selectedIssueIds.includes(id));

        try {
          // 添加新的議題關聯
          if (toAdd.length > 0) {
            await addIssuesToWorkLog(savedWorkLog.id, toAdd);
          }

          // 移除不再需要的議題關聯
          if (toRemove.length > 0) {
            await Promise.all(
              toRemove.map(issueId => removeIssueFromWorkLog(savedWorkLog.id, issueId))
            );
          }
        } catch (error) {
          console.error('Failed to update issue associations:', error);
          showError('更新議題關聯失敗');
        }
      }

      onSave();
    } catch (error: any) {
      console.error('Save work log error:', error);
      if (error.message?.includes('JSON')) {
        showError('JSON 格式錯誤，請檢查工作項目內容');
      } else {
        showError(workLog ? '更新失敗' : '建立失敗');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b">
            <Dialog.Title className="text-xl font-semibold">
              {workLog ? '編輯工作日誌' : '新增工作日誌'}
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* 基本資訊 */}
              <div>
                <h3 className="text-lg font-medium mb-4">基本資訊</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      標題 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入工作日誌標題"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      開始日期 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      結束日期 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      狀態
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as WorkLogStatus })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={WorkLogStatus.DRAFT}>草稿</option>
                      <option value={WorkLogStatus.PUBLISHED}>已發布</option>
                      <option value={WorkLogStatus.ARCHIVED}>已封存</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 社區資訊 */}
              <div>
                <h3 className="text-lg font-medium mb-4">社區資訊</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      社區名稱
                    </label>
                    <input
                      type="text"
                      value={formData.communityName}
                      onChange={(e) => setFormData({ ...formData, communityName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入社區名稱"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      管理公司
                    </label>
                    <input
                      type="text"
                      value={formData.managementCompany}
                      onChange={(e) => setFormData({ ...formData, managementCompany: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入管理公司名稱"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      值勤人員
                    </label>
                    <input
                      type="text"
                      value={formData.dutyStaff}
                      onChange={(e) => setFormData({ ...formData, dutyStaff: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="輸入值勤人員姓名"
                    />
                  </div>
                </div>
              </div>

              {/* 相關議題 */}
              <div>
                <h3 className="text-lg font-medium mb-4">相關議題</h3>
                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => setIsIssueSelectorOpen(true)}
                    className="flex items-center px-4 py-2 border border-dashed border-gray-300 rounded-md hover:border-gray-400 text-gray-600 hover:text-gray-800"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    選擇議題
                  </button>
                  {selectedIssueIds.length > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      已選擇 {selectedIssueIds.length} 個議題
                    </p>
                  )}
                </div>
              </div>

              {/* 工作項目內容 */}
              <div>
                <h3 className="text-lg font-medium mb-4">工作項目內容</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      上午工作事項
                    </label>
                    <textarea
                      value={formData.morningWorkItems}
                      onChange={(e) => setFormData({ ...formData, morningWorkItems: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="請輸入上午工作事項，可換行輸入多個項目"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      下午工作事項
                    </label>
                    <textarea
                      value={formData.afternoonWorkItems}
                      onChange={(e) => setFormData({ ...formData, afternoonWorkItems: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="請輸入下午工作事項，可換行輸入多個項目"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      其他工作事項
                    </label>
                    <textarea
                      value={formData.otherWorkItems}
                      onChange={(e) => setFormData({ ...formData, otherWorkItems: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="請輸入其他工作事項，可換行輸入多個項目"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isSubmitting}
              >
                取消
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? '處理中...' : (workLog ? '更新' : '建立')}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>

      {/* 議題選擇器 */}
      <IssueSelector
        projectId={projectId}
        selectedIssueIds={selectedIssueIds}
        isOpen={isIssueSelectorOpen}
        onClose={() => setIsIssueSelectorOpen(false)}
        onSelect={(ids) => {
          setSelectedIssueIds(ids);
          setIsIssueSelectorOpen(false);
        }}
      />
    </Dialog>
  );
};

export default WorkLogEditor;